import { useEffect, useState } from "react";
import type { Lang } from "../../lib/i18n";
import { pick } from "../../lib/i18n";
import { getAuditLog, subscribeAudit, clearAuditLog, exportAuditCSV } from "../lib/audit";
import { AUDIT_ACTIONS, relativeTime } from "../lib/format";

/** Audit Log tab (bt) — searchable, filterable, exportable local action log. */
export function AuditTab({ lang }: { lang: Lang }) {
  const t = (ar: string, en: string) => pick(lang, ar, en);
  const [entries, setEntries] = useState(() => getAuditLog());
  const [query, setQuery] = useState("");
  const [action, setAction] = useState("");
  useEffect(() => subscribeAudit(() => setEntries(getAuditLog())), []);

  const filtered = entries.filter((e) => {
    if (action && e.action !== action) return false;
    if (query) {
      const q = query.toLowerCase();
      if (!e.target.toLowerCase().includes(q) && !(e.detail || "").toLowerCase().includes(q) && !e.actor.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  function exportCsv() {
    const blob = new Blob(["﻿" + exportAuditCSV()], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `kaden-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }
  function clear() {
    if (confirm(t("سيتم مسح كل سجل التدقيق. متأكد؟", "All audit log entries will be deleted. Are you sure?"))) clearAuditLog();
  }

  return (
    <div className="kd-admin-audit">
      <div className="kd-admin-info-banner">
        <strong>{t("ملاحظة:", "Note:")}</strong>{" "}
        {t("يُسجَّل كل إجراء تقوم به من لوحة الإدارة هنا تلقائياً (أحدث 500 إجراء).", "Every admin action is logged here automatically (latest 500 entries).")}
      </div>
      <div className="kd-admin-toolbar">
        <input type="text" className="kd-admin-search" placeholder={t("بحث في السجل...", "Search log...")} value={query} onChange={(e) => setQuery(e.target.value)} />
        <select value={action} onChange={(e) => setAction(e.target.value)} className="kd-admin-filter">
          <option value="">{t("كل الإجراءات", "All actions")}</option>
          {Object.entries(AUDIT_ACTIONS).map(([id, a]) => (
            <option value={id} key={id}>{lang === "ar" ? a.ar : a.en}</option>
          ))}
        </select>
        <button className="kd-btn-secondary" onClick={exportCsv} disabled={entries.length === 0}>↓ {t("تصدير CSV", "Export CSV")}</button>
        <button className="kd-btn-secondary kd-btn-danger" onClick={clear} disabled={entries.length === 0}>{t("مسح السجل", "Clear log")}</button>
      </div>
      <div className="kd-admin-table-wrap">
        <table className="kd-admin-table">
          <thead>
            <tr><th>{t("الوقت", "Time")}</th><th>{t("المنفّذ", "Actor")}</th><th>{t("الإجراء", "Action")}</th><th>{t("الهدف", "Target")}</th><th>{t("التفاصيل", "Detail")}</th></tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={5} style={{ textAlign: "center", padding: "2rem", color: "var(--kd-text-muted)" }}>{t("لا يوجد سجل.", "No log entries.")}</td></tr>}
            {filtered.map((e) => {
              const meta = AUDIT_ACTIONS[e.action];
              return (
                <tr key={e.id}>
                  <td><small title={new Date(e.timestamp).toLocaleString()}>{relativeTime(e.timestamp, lang)}</small></td>
                  <td>{e.actor}</td>
                  <td><span className="kd-admin-badge" style={meta ? { color: meta.color, borderColor: `${meta.color}55`, background: `${meta.color}18` } : undefined}>{meta ? (lang === "ar" ? meta.ar : meta.en) : e.action}</span></td>
                  <td>{e.target || "—"}</td>
                  <td><small>{e.detail || "—"}</small></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
